'use client'

import React, { useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  Anchor,
  Box,
  Button,
  Group,
  Image,
  List,
  Modal,
  Paper,
  RingProgress,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core'
import { Dropzone, MIME_TYPES } from '@mantine/dropzone'
import { useOs } from '@mantine/hooks'
import { faCircleCheck, faFilePdf, faImage, faUpload } from '@fortawesome/pro-light-svg-icons'
import { faCheck, faCircleXmark, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Uploady, {
  BatchItem,
  useAbortItem,
  useItemErrorListener,
  useItemFinishListener,
  useItemProgressListener,
  useItemStartListener,
  useRequestPreSend,
  useUploady,
} from '@rpldy/uploady'
import { notifyError, notifySuccess } from '@phoenix-web/react-shared/client-components'
import { redactMediaProps, redactPropsWithClassName } from '@phoenix-web/utils/general'
import { useGetSignedFileUploadUrls, useUploadTransactionPhotos } from '@phoenix-web/ux-gql/hooks'
import styles from './PhotoUploader.module.css'

export type AcceptedPhotoTypes = typeof MIME_TYPES.jpeg | typeof MIME_TYPES.png | typeof MIME_TYPES.webp

export type AllAcceptedFileTypes = AcceptedPhotoTypes | typeof MIME_TYPES.pdf

export const ACCEPTED_PHOTO_TYPES: AcceptedPhotoTypes[] = [MIME_TYPES.jpeg, MIME_TYPES.png, MIME_TYPES.webp]

export const ALL_ACCEPTED_FILE_TYPES: AllAcceptedFileTypes[] = [...ACCEPTED_PHOTO_TYPES, MIME_TYPES.pdf]

// Single source for the per-file size limit, so the Dropzone rule and the helper copy cannot drift.
// Must match Jupiter's FILE_UPLOAD_SETTINGS.MAX_FILE_SIZE (src/config/constants.ts), which S3
// enforces via the presigned POST's content-length-range condition. A larger client limit lets
// files through the dropzone that S3 then rejects with an opaque 403.
const MAX_FILE_SIZE_MB = 20

type UploadItemType = {
  fileSize: number
  id: string
  name: string
  progress: number
  status: 'uploading' | 'done' | 'error'
  preview?: string
  fileLocation?: string
  presignedFormData?: any
  contentType?: string
}

function UploadyDropzone({ transactionId, disabled }: { transactionId: number; disabled?: boolean }) {
  const uploady = useUploady()
  const os = useOs({ getValueInEffect: false })
  const isMobile = os === 'android' || os === 'ios'
  const acceptedFileTypes = isMobile ? ACCEPTED_PHOTO_TYPES : ALL_ACCEPTED_FILE_TYPES
  const [getSignedFileUploadUrls] = useGetSignedFileUploadUrls()
  const [uploadedFiles, setUploadedFiles] = useState<Set<string>>(new Set())
  // Names already assigned to files this session. Camera captures ("Take Photo" on mobile) all
  // reuse a generic name (e.g. iOS names every capture `image.jpg`), so we rename collisions to a
  // unique name before uploading. This keeps the DB `original_file_name` meaningful and avoids
  // relying on the client only ever presigning one file per request to satisfy Jupiter's
  // "Files must be unique" check.
  const usedNames = useRef<Set<string>>(new Set())

  useRequestPreSend(async ({ items, options }) => {
    const item = items[0]

    try {
      const presignedUrlResponse = await getSignedFileUploadUrls({
        variables: {
          input: {
            transaction_id: transactionId,
            files: [
              {
                file_name: item.file.name,
                content_type: item.file.type,
              },
            ],
          },
        },
      })

      const uploadedFile = presignedUrlResponse.data?.getSignedFileUploadUrls?.files[0]
      const presignedFormData = uploadedFile?.presigned_form_data
      const fileLocation = uploadedFile?.file_location

      if (!presignedFormData) {
        console.error('Failed to get presigned form data', presignedUrlResponse.data)
        throw new Error('Failed to get presigned form data')
      }

      if (!fileLocation) {
        console.error('Failed to get file location', presignedUrlResponse.data)
        throw new Error('Failed to get file location')
      }

      let fields: Record<string, string>
      try {
        fields = JSON.parse(presignedFormData.fields) as Record<string, string>
      } catch (parseError) {
        console.error('Failed to parse presigned form fields', parseError)
        throw new Error('Failed to parse presigned form fields')
      }

      // Configure uploady for S3 presigned POST
      const result = {
        items: [
          {
            ...item,
            userData: {
              fileLocation,
              presignedFormData,
            },
          },
        ],
        options: {
          ...options,
          destination: {
            ...options.destination,
            url: presignedFormData.url,
            method: 'POST',
            // Add the presigned fields as params (Uploady will include them in FormData)
            params: fields,
          },
          // Important: Set to true since S3 expects multipart/form-data
          sendWithFormData: true,
          // The file field name that S3 expects (usually 'file')
          paramName: 'file',
          // Optional: Handle CORS if needed
          withCredentials: false,
        },
      }

      return result
    } catch (error) {
      console.error('Error in useRequestPreSend:', error)

      // The presign mutation goes through the app's /api/graphql proxy, which returns 401 once
      // the Auth0 session lapses. Photo upload is a long-dwell flow (open the page, go take
      // photos, come back), so the 30 minute rolling inactivity window can expire between page
      // render and the user dropping files. Without this the upload just dies silently.
      const statusCode = (error as { networkError?: { statusCode?: number } })?.networkError?.statusCode
      if (statusCode === 401) {
        notifyError({
          id: 'photo-upload-session-expired',
          title: 'Your session has expired',
          message: 'Refresh the page to sign back in, then add your photos again.',
        })
      }

      throw error
    }
  })
  // Build a stable identity for a file. Camera captures ("Take Photo" on mobile) all reuse the
  // same generic filename (e.g. iOS names every capture `image.jpg`), so keying dedup on the name
  // alone drops every photo after the first. Include size and lastModified so distinct captures
  // are treated as distinct files, while a genuine re-selection of the same file is still caught.
  const getFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`
  // Return a File whose name is unique for this session, renaming only on collision so files that
  // already have distinct names (e.g. picked from the photo library) keep them. Colliding names
  // get a `-N` suffix before the extension, e.g. `image.jpg` -> `image-1.jpg`.
  const ensureUniqueName = (file: File): File => {
    if (!usedNames.current.has(file.name)) {
      usedNames.current.add(file.name)
      return file
    }
    const dot = file.name.lastIndexOf('.')
    const base = dot > 0 ? file.name.slice(0, dot) : file.name
    const ext = dot > 0 ? file.name.slice(dot) : ''
    let counter = 1
    let candidate = `${base}-${counter}${ext}`
    while (usedNames.current.has(candidate)) {
      counter += 1
      candidate = `${base}-${counter}${ext}`
    }
    usedNames.current.add(candidate)
    return new File([file], candidate, { type: file.type, lastModified: file.lastModified })
  }
  return (
    <Dropzone
      multiple
      disabled={disabled}
      onDrop={(files) => {
        // Drop genuine re-selections of the same picture, matched on the composite key of the
        // *original* file. Seed from already-accepted files and keep accumulating within this batch
        // so the exact same file can't slip through, whether re-selected later or (defensively)
        // present twice in one selection. Survivors are then given unique names so camera captures
        // sharing `image.jpg` still upload as distinct files.
        const seen = new Set(uploadedFiles)
        const uniqueFiles = files.filter((file) => {
          const key = getFileKey(file)
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })

        if (uniqueFiles.length > 0) {
          setUploadedFiles(seen)
          uploady.upload(uniqueFiles.map((file) => ensureUniqueName(file)))
        }
      }}
      onReject={(files) => console.error('onReject', files)}
      maxSize={MAX_FILE_SIZE_MB * 1024 ** 2}
      accept={acceptedFileTypes}
      useFsAccessApi={!isMobile}
      radius="md"
      p={0}
      style={{
        minHeight: 180,
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 220,
      }}
    >
      <Group maw="80%" mx="auto" align="center">
        <Dropzone.Accept>
          <ThemeIcon size={64} variant="transparent" color="blue" style={{ alignSelf: 'flex-start' }}>
            <FontAwesomeIcon icon={faUpload} size="3x" />
          </ThemeIcon>
        </Dropzone.Accept>
        <Dropzone.Reject>
          <ThemeIcon size={64} variant="transparent" color="red" style={{ alignSelf: 'flex-start' }}>
            <FontAwesomeIcon icon={faXmark} size="3x" />
          </ThemeIcon>
        </Dropzone.Reject>
        <Dropzone.Idle>
          <ThemeIcon size={64} variant="transparent" color="dark.3" style={{ alignSelf: 'flex-start' }}>
            <FontAwesomeIcon icon={faImage} size="3x" />
          </ThemeIcon>
        </Dropzone.Idle>
        <Box style={{ flex: 1 }}>
          <Text fw={500} size="lg" style={{ color: '#101828' }}>
            Drag images here or click to select files
          </Text>
          <Text size="md" c="#98A2B3" mt={4}>
            Attach as many files as you like, each file should not exceed {MAX_FILE_SIZE_MB} MB. Selected files are
            listed below.
          </Text>
        </Box>
      </Group>
    </Dropzone>
  )
}

// 2) A component that subscribes to Uploady listeners and renders progress + cancel
function UploadProgressList({
  onFilesChange,
  onAbort,
}: {
  onFilesChange?: (items: UploadItemType[]) => void
  onAbort?: (id: string) => void
}) {
  const [items, setItems] = useState<UploadItemType[]>([])
  const abortItem = useAbortItem()

  // Remove item from list and abort upload
  const handleAbort = (id: string) => {
    abortItem(id)
    setItems((list) => list.filter((it) => it.id !== id))
    // Notify parent about the abort
    if (onAbort) onAbort(id)
  }

  // When an item starts, add it to our state
  useItemStartListener((startEvent) => {
    // Only create preview for image files, not PDFs
    const preview = ACCEPTED_PHOTO_TYPES.includes(startEvent.file.type as AcceptedPhotoTypes)
      ? URL.createObjectURL(startEvent.file as File)
      : undefined

    // @ts-expect-error TODO: fix this
    const presignedFormData = startEvent.file?.userData?.presignedFormData
    // @ts-expect-error TODO: fix this
    const fileLocation = startEvent.file?.userData?.fileLocation
    setItems((list) => [
      {
        id: startEvent.id,
        name: startEvent.file.name,
        progress: 0,
        status: 'uploading',
        preview,
        fileLocation,
        presignedFormData,
        contentType: startEvent.file.type,
        fileSize: startEvent.file.size,
      },
      ...list,
    ])
  })

  // Progress updates
  useItemProgressListener((progressEvent) => {
    setItems((list) =>
      list.map((it) =>
        it.id === progressEvent.id
          ? { ...it, progress: Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1)) }
          : it
      )
    )
  })

  // When finished, mark as done
  useItemFinishListener((finishEvent) => {
    setItems((list) =>
      list.map((it) =>
        it.id === finishEvent.id
          ? {
              ...it,
              progress: 100,
              status: [200, 204].includes(finishEvent.uploadStatus) ? 'done' : 'error',
              // If the upload failed, keep the preview URL
              // preview: finishEvent.uploadStatus === 200 ? undefined : it.preview,
              fileLocation: (finishEvent as BatchItem & { userData: { fileLocation: string } })?.userData?.fileLocation,
              contentType: finishEvent.file.type,
            }
          : it
      )
    )
  })

  useItemErrorListener((errorEvent) => {
    console.error('useItemErrorListener errorEvent', errorEvent)
    setItems((list) => list.map((it) => (it.id === errorEvent.id ? { ...it, status: 'error' } : it)))
  })

  // Notify parent of file changes
  React.useEffect(() => {
    if (onFilesChange) onFilesChange(items)
  }, [items, onFilesChange])

  // Cleanup preview URLs when component unmounts
  React.useEffect(() => {
    return () => {
      items.forEach((item) => {
        if (item.preview) {
          URL.revokeObjectURL(item.preview)
        }
      })
    }
  }, [items])

  if (items.length === 0) return null

  return (
    <Box mt="md">
      <ScrollArea h={260} type="hover" offsetScrollbars scrollbarSize={8}>
        <Stack gap={0}>
          {items.map((it, idx) => (
            <Group
              key={it.id}
              py="sm"
              px="xs"
              align="center"
              wrap="nowrap"
              style={{ borderBottom: idx !== items.length - 1 ? '1px solid #f1f3f5' : undefined }}
            >
              {/* RingProgress for upload status */}
              <Box mr={0} style={{ minWidth: 36, maxWidth: 36 }}>
                {it.status === 'done' ? (
                  <RingProgress
                    size={36}
                    thickness={4}
                    sections={[{ value: 100, color: 'blue' }]}
                    label={
                      <ThemeIcon color="blue" size={26} ml={-2} radius="xl" variant="light">
                        <FontAwesomeIcon icon={faCheck} size="1x" />
                      </ThemeIcon>
                    }
                  />
                ) : it.status === 'error' ? (
                  <RingProgress
                    size={36}
                    thickness={4}
                    sections={[{ value: 100, color: 'red' }]}
                    label={
                      <ThemeIcon color="red" size={26} ml={-2} radius="xl" variant="light">
                        <FontAwesomeIcon icon={faXmark} size="1x" />
                      </ThemeIcon>
                    }
                  />
                ) : (
                  <RingProgress
                    size={36}
                    thickness={4}
                    sections={[{ value: it.progress, color: 'blue' }]}
                    // label={
                    //   <Text size="xxs" fw={700} c="gray" ta="center">
                    //     {it.progress}%
                    //   </Text>
                    // }
                  />
                )}
              </Box>
              {/* Image thumbnail */}
              {it.preview && (
                <Image
                  {...redactMediaProps}
                  src={it.preview}
                  alt={it.name}
                  width={40}
                  height={40}
                  radius="md"
                  mr="sm"
                  style={{ objectFit: 'cover', minWidth: 40, minHeight: 40, maxWidth: 40, maxHeight: 40 }}
                />
              )}
              {/* PDF icon */}
              {it.contentType === MIME_TYPES.pdf && (
                <Box
                  mr="sm"
                  style={{
                    minWidth: 40,
                    minHeight: 40,
                    maxWidth: 40,
                    maxHeight: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FontAwesomeIcon icon={faFilePdf} size="xl" />
                </Box>
              )}
              <Box style={{ flex: 1 }}>
                <Text size="sm" fw={500} lineClamp={1} {...redactPropsWithClassName}>
                  {it.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {(it.fileSize / (1024 * 1024)).toFixed(1)} MB
                </Text>
              </Box>
              {/* {it.status === 'uploading' && ( */}
              <Button size="xs" color="gray" variant="subtle" ml={4} onClick={() => handleAbort(it.id)}>
                <FontAwesomeIcon icon={faCircleXmark} size="lg" />
              </Button>
              {/* )} */}
            </Group>
          ))}
        </Stack>
      </ScrollArea>
    </Box>
  )
}

export type PhotoUploaderProps = {
  numberOfExistingFiles: number
  transactionId: number
  redirectPath: string
  maxPhotos: number
  // Optional consumer-supplied content rendered above the requirements (e.g. a borrower-facing
  // "Quick tip" callout). Omitted by consumers like backyard2 that shouldn't show it.
  tip?: React.ReactNode
  // When true, submitting opens a "Done uploading?" confirmation before the upload runs.
  // Defaults to false so consumers that don't opt in keep the direct submit + redirect behavior.
  confirmOnSubmit?: boolean
  // App-owned side effect after a successful save (e.g. Heap tracking). Omitted by consumers
  // that shouldn't fire analytics. Invoked from a client wrapper so the callback never crosses
  // the RSC boundary.
  onUploadSuccess?: (payload: { transactionId: number; photoCount: number }) => void
}

// 3) The top‐level Uploader that ties it all together
export default function S3UploadyWithMantine({
  numberOfExistingFiles,
  transactionId,
  redirectPath,
  maxPhotos,
  tip,
  confirmOnSubmit = false,
  onUploadSuccess,
}: PhotoUploaderProps) {
  const router = useRouter()
  // Photos already persisted to our DB — those saved on a previous visit (the prop) plus any batch
  // the user saves this session and then chooses to keep uploading past. Like previously-saved
  // photos, these are reflected in the "remaining" count but not shown as thumbnails.
  const [existingCount, setExistingCount] = React.useState(numberOfExistingFiles)
  const [numberOfPhotosRemaining, setNumberOfPhotosRemaining] = React.useState(maxPhotos - numberOfExistingFiles)
  const [files, setFiles] = React.useState<UploadItemType[]>([])
  const [uploadTransactionPhotos, { loading, error, data }] = useUploadTransactionPhotos()
  const [confirmOpened, setConfirmOpened] = React.useState(false)
  // Bumping this remounts <Uploady> to reset its internal state and the child list/dedup set,
  // giving the user a clean list after they save a batch and choose "No, upload more".
  const [uploaderKey, setUploaderKey] = React.useState(0)
  const totalPhotosCount = existingCount + files.filter((f) => f.status === 'done').length

  // Only allow submit if all files are done and we haven't exceeded the photo limit
  const canSubmit = files.length > 0 && files.every((f) => f.status === 'done') && totalPhotosCount <= maxPhotos

  // Handle file changes to update numberOfPhotosRemaining
  const handleFilesChange = (newFiles: UploadItemType[]) => {
    setFiles(newFiles)

    // Calculate how many photos are currently being uploaded or completed
    const activeFiles = newFiles.filter((f) => f.status === 'uploading' || f.status === 'done')
    const newRemaining = maxPhotos - existingCount - activeFiles.length

    setNumberOfPhotosRemaining(Math.max(0, newRemaining))
  }

  // Handle file abort to increment numberOfPhotosRemaining
  const handleFileAbort = (id: string) => {
    setNumberOfPhotosRemaining((prev) => Math.min(maxPhotos - existingCount, prev + 1))
  }

  // Clicking "Submit Photos" persists the finished files to S3 records + our DB *first*, then
  // either opens the "Done uploading?" confirmation (confirmOnSubmit) or redirects. Saving before
  // the modal means photos aren't lost if the user picks "No, upload more" and their session later
  // expires. The list only ever holds not-yet-saved files (see handleUploadMore), so submitting the
  // whole list can't create duplicate records.
  const handleSubmit = async () => {
    if (!canSubmit) return

    const doneFiles = files.filter((f) => f.status === 'done')
    const filesToUpload = doneFiles.map((f) => ({
      file_location: f.fileLocation,
      file_name: f.name,
      content_type: f.contentType,
    }))
    try {
      const result = await uploadTransactionPhotos({
        variables: { input: { transaction_id: transactionId, files: filesToUpload } },
      })
      if (result.data?.uploadTransactionFiles?.success) {
        try {
          onUploadSuccess?.({ transactionId, photoCount: filesToUpload.length })
        } catch {
          // Consumer callbacks (analytics) must not block the save/redirect path.
        }

        finishOrConfirm()
      }
      // Non-success falls through: the on-page `error` message stays visible and no modal opens.
    } catch {
      // Swallow the rejection — the mutation hook surfaces it via `error` on the page below.
    }
  }

  // After a successful save, ask the user whether they're done (confirmOnSubmit) or immediately
  // notify + redirect for consumers that don't opt into the confirmation step.
  const finishOrConfirm = () => {
    if (confirmOnSubmit) {
      setConfirmOpened(true)
    } else {
      handleFinish()
    }
  }

  // "No, upload more": the current batch is already saved, so fold it into the existing count and
  // reset the uploader to an empty list. Starting fresh keeps saved photos from being re-submitted
  // and avoids a confusing list that mixes already-saved photos with new ones.
  const handleUploadMore = () => {
    const savedCount = files.filter((f) => f.status === 'done').length
    setExistingCount((prev) => prev + savedCount)
    setFiles([])
    setNumberOfPhotosRemaining(Math.max(0, maxPhotos - existingCount - savedCount))
    setUploaderKey((k) => k + 1)
    setConfirmOpened(false)
  }

  // Leaves for the next step. Photos are already persisted by the time this runs, so choosing
  // "Yes, continue" in the modal only needs to notify + redirect.
  const handleFinish = () => {
    notifySuccess({
      message: 'Your photos were uploaded successfully.',
      autoClose: 10000,
    })
    router.replace(redirectPath)
  }

  return (
    <Uploady key={uploaderKey}>
      <Box maw={800} pt="xl">
        {tip}
        <Group align="flex-start" mb="md">
          <Text fw={700} size="md">
            Photo Requirements
          </Text>
          <List>
            <List.Item>Photos must be taken within the last 3 months.</List.Item>
            <List.Item>
              Provide 2 photos of each room (kitchen, bedrooms, bathrooms, etc.), capturing the entire layout, and key
              features.
            </List.Item>
            <List.Item>
              Provide 2 photos each of the:
              <List withPadding listStyleType="disc">
                <List.Item>Front of house</List.Item>
                <List.Item>Rear of house</List.Item>
                <List.Item>Backyard</List.Item>
                <List.Item>Garage/parking area</List.Item>
              </List>
            </List.Item>
          </List>
        </Group>
        {/* Dropzone Section */}
        <Stack align="flex-stretch" gap="0" mb="xl">
          <Paper withBorder radius="md" p={0} style={{ borderStyle: 'dashed', borderWidth: 2 }}>
            <UploadyDropzone transactionId={transactionId} disabled={existingCount >= maxPhotos} />
          </Paper>
          <Text size="md" c="dark.4" mt={4} ta={'right'}>
            {numberOfPhotosRemaining}/{maxPhotos} uploads remaining
          </Text>
        </Stack>
        {/* Upload List Section */}
        <UploadProgressList onFilesChange={handleFilesChange} onAbort={handleFileAbort} />
        {/* Submit Button */}

        <Group justify="flex-end" mt="md">
          <Box>
            {/* Optionally show error or success */}
            {error && <Text c="red">Error uploading photos</Text>}
            {existingCount >= maxPhotos && (
              <Text c="red">You have reached the maximum number of allowed photos ({maxPhotos}).</Text>
            )}
            {totalPhotosCount >= maxPhotos + 1 && (
              <Text c="red">
                You have exceeded the maximum number of allowed photos ({maxPhotos}). Please delete{' '}
                {totalPhotosCount - maxPhotos} photo(s) to continue.
              </Text>
            )}
          </Box>
          <Button onClick={handleSubmit} disabled={!canSubmit} loading={loading} size="md" variant="filled">
            Submit Photos
          </Button>
        </Group>
      </Box>
      {confirmOnSubmit && (
        <Modal
          opened={confirmOpened}
          // The save already ran before this modal opened, so any dismissal (Escape, close button,
          // backdrop) means "stay and maybe upload more" — reset to a clean list exactly like the
          // "No, upload more" button, so the saved batch can't be re-submitted.
          onClose={handleUploadMore}
          title="Done uploading photos?"
          centered
          radius="md"
          styles={{
            title: {
              fontFamily: 'var(--mantine-font-family-headings)',
              fontSize: 'var(--mantine-h3-font-size)',
              fontWeight: 700,
            },
          }}
        >
          <Stack gap="sm">
            <Text size="sm" c="dark.8">
              Your photos have been saved. You can upload more before continuing to the next step.
            </Text>
            <Button size="md" fullWidth onClick={handleFinish}>
              Yes, continue
            </Button>
            <Button variant="default" size="md" fullWidth onClick={handleUploadMore}>
              No, upload more
            </Button>
          </Stack>
        </Modal>
      )}
    </Uploady>
  )
}
